import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Combobox } from '@/components/ui/combobox'
import { CheckCircle } from 'lucide-react'

// Default tax configuration constants
export const DEFAULT_TAX_TABLE = 'skattetabell31'
export const DEFAULT_TAX_COLUMN = 1

interface TaxCalculationConfigProps {
  value: {
    taxCalculationMethod?: 'fixed' | 'table'
    taxRate?: number
    taxTableName?: string
    taxColumn?: number
  }
  onChange: (field: string, value: any) => void
  availableTables: number[]
  taxTableLoading: boolean
  taxTableLoaded: boolean
  disabled?: boolean
  showPlaceholder?: boolean  // Show "leave empty for global" hints (for driver mode)
  compact?: boolean          // Less spacing for use in dialogs
  rawInputs?: {
    taxRate: string
  }
  onRawInputChange?: (field: string, value: string) => void
}

export function TaxCalculationConfig({
  value,
  onChange,
  availableTables,
  taxTableLoading,
  taxTableLoaded,
  disabled = false,
  showPlaceholder = false,
  compact = false,
  rawInputs,
  onRawInputChange
}: TaxCalculationConfigProps) {
  const gridGap = compact ? 'gap-3' : 'gap-3'
  const spacing = compact ? 'space-y-1.5' : 'space-y-1.5'

  // Use raw input if provided, otherwise use value
  const displayTaxRate = rawInputs ? rawInputs.taxRate : (value.taxRate ?? '')

  return (
    <div className={`grid ${gridGap}`}>
      {/* Tax Calculation Method */}
      <div className={spacing}>
        <Label htmlFor="taxCalculationMethod" className="font-medium">Skatteberäkning</Label>
        <RadioGroup
          value={value.taxCalculationMethod ?? 'fixed'}
          onValueChange={(val: 'fixed' | 'table') => {
            onChange('taxCalculationMethod', val)
            // When switching to table method, set default values if not already set
            if (val === 'table') {
              if (!value.taxTableName) {
                onChange('taxTableName', DEFAULT_TAX_TABLE)
              }
              if (!value.taxColumn) {
                onChange('taxColumn', DEFAULT_TAX_COLUMN)
              }
            }
          }}
          className="flex space-x-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="fixed" id="tax-fixed" disabled={disabled} />
            <Label htmlFor="tax-fixed" className="text-sm font-normal">Fast procentsats</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="table" id="tax-table" disabled={disabled} />
            <Label htmlFor="tax-table" className="text-sm font-normal">Skattetabell</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Fixed Tax Rate */}
      {value.taxCalculationMethod !== 'table' && (
        <div className={spacing}>
          <Label htmlFor="taxRate" className="font-medium">
            Skattesats (%)
            {!showPlaceholder && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            id="taxRate"
            type="number"
            value={displayTaxRate}
            onChange={(e) => {
              if (onRawInputChange) {
                onRawInputChange('taxRate', e.target.value)
              }
              const val = e.target.value === '' ? undefined : parseFloat(e.target.value)

              // When user enters a tax rate, ensure taxCalculationMethod is set to 'fixed'
              if (val !== undefined && value.taxCalculationMethod === undefined) {
                onChange('taxCalculationMethod', 'fixed')
              }

              onChange('taxRate', val)
            }}
            onBlur={(e) => {
              const val = parseFloat(e.target.value)
              if (!isNaN(val)) {
                const clamped = Math.min(100, Math.max(0, val))

                // When user enters a tax rate, ensure taxCalculationMethod is set to 'fixed'
                if (value.taxCalculationMethod === undefined) {
                  onChange('taxCalculationMethod', 'fixed')
                }

                onChange('taxRate', clamped)
              }
            }}
            min="0"
            max="100"
            step="1"
            disabled={disabled}
            placeholder={showPlaceholder ? "T.ex. 30" : "T.ex. 30"}
            required={!showPlaceholder}
          />
          {showPlaceholder && (
            <p className="text-xs text-gray-500">
              Lämna tomt för att använda global inställning
            </p>
          )}
        </div>
      )}

      {/* Tax Table Settings */}
      {value.taxCalculationMethod === 'table' && (
        <div className="space-y-3 p-3 bg-muted/20 rounded-lg border border-border/50">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Skattetabell</Label>
            {taxTableLoading ? (
              <Badge variant="outline" className="gap-1">
                <div className="animate-spin rounded-full h-2 w-2 border-b border-current"></div>
              </Badge>
            ) : taxTableLoaded ? (
              <Badge variant="secondary" className="gap-1">
                <CheckCircle className="h-3 w-3" />
              </Badge>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={spacing}>
              <Label htmlFor="taxTableSelect" className="text-sm">Tabell</Label>
              <Combobox
                options={availableTables.map(tableNumber => ({
                  value: tableNumber.toString(),
                  label: `Tabell ${tableNumber}`
                }))}
                value={value.taxTableName?.replace(/\D/g, '') || '31'}
                onValueChange={(val) => onChange('taxTableName', `skattetabell${val}`)}
                placeholder="Välj tabell"
                searchPlaceholder="Sök..."
                disabled={disabled}
              />
            </div>

            <div className={spacing}>
              <Label htmlFor="taxColumnSelect" className="text-sm">Kolumn</Label>
              <Combobox
                options={[1, 2, 3, 4, 5, 6].map(column => ({
                  value: column.toString(),
                  label: `Kolumn ${column}`
                }))}
                value={(value.taxColumn || 1).toString()}
                onValueChange={(val) => onChange('taxColumn', parseInt(val))}
                placeholder="Välj kolumn"
                searchPlaceholder="Sök..."
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
